import * as signalR from '@microsoft/signalr';
import type { AppNotification } from './api';
import { getApiBaseUrlNoPath } from './api';

type HubHandlers = {
  onNotification: (notification: AppNotification) => void;
  onUnreadCount: (count: number) => void;
};

let hub: signalR.HubConnection | null = null;
let hubEmployeeId: number | null = null;
let hubRefCount = 0;
let stopTimer: ReturnType<typeof setTimeout> | null = null;
let handlers: HubHandlers | null = null;
let notificationDataLoadedFor: number | null = null;

/** True until unread badge has successfully been requested for this employee. */
export function shouldLoadNotificationData(employeeId: number): boolean {
  return notificationDataLoadedFor !== employeeId;
}

export function markNotificationDataLoaded(employeeId: number): void {
  notificationDataLoadedFor = employeeId;
}

export function resetNotificationSession(): void {
  notificationDataLoadedFor = null;
}

function attachHandlers(connection: signalR.HubConnection) {
  connection.off('ReceiveNotification');
  connection.off('UpdateUnreadCount');
  if (!handlers) return;
  connection.on('ReceiveNotification', handlers.onNotification);
  connection.on('UpdateUnreadCount', ({ count }: { count: number }) => {
    handlers?.onUnreadCount(count);
  });
}

export function acquireNotificationHub(
  employeeId: number,
  nextHandlers: HubHandlers
): signalR.HubConnection | null {
  handlers = nextHandlers;
  hubRefCount += 1;
  if (stopTimer) {
    clearTimeout(stopTimer);
    stopTimer = null;
  }

  const token = localStorage.getItem('authToken');
  const baseUrl = getApiBaseUrlNoPath();
  if (!token || !baseUrl) return null;

  if (hub && hubEmployeeId === employeeId) {
    attachHandlers(hub);
    return hub;
  }

  if (hub) {
    hub.stop().catch(() => {});
    hub = null;
    hubEmployeeId = null;
  }

  // WebSockets only — LongPolling holds an HTTP connection Pending for minutes and
  // starves the browser's ~6 connections/origin, delaying dashboard APIs.
  hub = new signalR.HubConnectionBuilder()
    .withUrl(`${baseUrl}/hubs/notifications`, {
      accessTokenFactory: () => token,
      transport: signalR.HttpTransportType.WebSockets,
    })
    .withAutomaticReconnect([1000, 2000, 5000, 10000])
    .configureLogging(signalR.LogLevel.Warning)
    .build();
  hubEmployeeId = employeeId;
  attachHandlers(hub);
  hub.start().catch((err) => {
    console.warn('[notifications] WebSocket hub failed to start:', err);
  });
  return hub;
}

export function releaseNotificationHub(): void {
  hubRefCount = Math.max(0, hubRefCount - 1);
  if (hubRefCount > 0) return;
  if (stopTimer) clearTimeout(stopTimer);
  stopTimer = setTimeout(() => {
    if (hubRefCount === 0 && hub) {
      hub.stop().catch(() => {});
      hub = null;
      hubEmployeeId = null;
    }
    stopTimer = null;
  }, 250);
}

export function stopNotificationHub(): void {
  hubRefCount = 0;
  notificationDataLoadedFor = null;
  if (stopTimer) {
    clearTimeout(stopTimer);
    stopTimer = null;
  }
  if (hub) {
    hub.stop().catch(() => {});
    hub = null;
    hubEmployeeId = null;
  }
}
